import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Monogram from "../ui/Monogram";
import { formatDay, today, tomorrow } from "../../utils/dates";

/**
 * One business in the discovery results.
 *
 * The openings are the point of the card. A visitor who searched for a day is
 * choosing between businesses on when they can be seen, so the times are
 * primary content and each one is its own control — clicking 10:00 gets that
 * business already set to 10:00, rather than making the visitor re-pick the
 * service, the day and the hour they just chose here.
 *
 * Whether that control is a link or a button is the caller's call, and it is the
 * one real difference between the two places this card appears:
 *
 * - **Public** (`/discover`, no `onSelectSlot`) — a link to the business's own
 *   booking page, carrying the choice in the query string. An anonymous visitor
 *   has nowhere else to be taken.
 * - **Signed in** (the `Book` tab, `onSelectSlot` given) — a button that opens
 *   the booking modal over the results, so the customer keeps their search.
 *   Leaving the dashboard for a marketing page mid-search is a worse trade for
 *   someone who is already signed in.
 *
 * The two must stay symmetrical: whatever a slot does, the whole card offers the
 * same destination for "just show me this business".
 */
const BusinessCard = ({ business, date, onSelectSlot }) => {
  const { t, i18n } = useTranslation();

  const image = business.headerImageUrl || business.logoUrl;
  const title = business.bookingTitle || business.businessName;
  const availability = business.availability;
  const slots = availability?.slots || [];
  const remaining = (availability?.slotCount || 0) - slots.length;

  // With no day searched, these are the soonest this business has rather than
  // "today" — so the card has to say which day, or the times mean nothing.
  const isSoonest = !date && Boolean(availability);
  const slotDate = availability?.date || date;

  // "Today" and "Tomorrow" read better than a date, and they are also the two
  // answers that make a business worth clicking right now.
  const dayLabel = () => {
    if (slotDate === today()) return t("Discover.today");
    if (slotDate === tomorrow()) return t("Discover.tomorrow");
    return formatDay(slotDate, i18n.language);
  };

  // Where a slot sends the visitor: the business's own page, with enough in the
  // query string for the booking modal to open on the choice they just made.
  const bookingHref = (slot) => {
    const params = new URLSearchParams({
      service: String(slot.serviceId),
      resource: String(slot.resourceId),
      date: slotDate,
      time: slot.value,
    });
    return `/${business.slug}?${params.toString()}`;
  };

  const slotClass =
    "discover-slot inline-flex cursor-pointer items-center rounded-lg border border-brand-200 bg-white px-3 py-2 text-sm font-semibold text-brand-700 transition hover:border-brand-600 hover:bg-brand-600 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600";

  /**
   * One opening. A button in the signed-in tab, a link on the public page — the
   * markup differs because the destinations genuinely differ, and rendering a
   * link that calls `preventDefault` would leave a misleading URL in the status
   * bar and break middle-click.
   *
   * `nofollow`, because every slot is a new URL — one per business, service,
   * day and time, and tomorrow brings a fresh set. Each canonicals to the
   * business page, so Google indexes none of them, but before this it crawled
   * them all (Search Console: "Alternate page with proper canonical tag"),
   * spending its visits on throwaway addresses instead of pages we want found.
   * The business link below stays followable: that page is the one to index.
   */
  const renderSlot = (slot) =>
    onSelectSlot ? (
      <button
        type="button"
        onClick={() => onSelectSlot(business, { ...slot, date: slotDate })}
        title={`${slot.time} — ${slot.serviceName}`}
        className={slotClass}
      >
        {slot.time}
      </button>
    ) : (
      <Link
        to={bookingHref(slot)}
        rel="nofollow"
        title={`${slot.time} — ${slot.serviceName}`}
        className={slotClass}
      >
        {slot.time}
      </Link>
    );

  /**
   * "Show me this business" — the card image, the title, the more-slots link,
   * the fallback CTA. In the signed-in tab it opens the same modal with nothing
   * preselected, so the customer picks a service and time there instead of
   * being sent off to the public site.
   */
  const renderBusinessLink = (children, className, extraProps = {}) =>
    onSelectSlot ? (
      <button
        type="button"
        onClick={() => onSelectSlot(business, null)}
        className={`cursor-pointer text-left ${className}`}
        {...extraProps}
      >
        {children}
      </button>
    ) : (
      <Link to={`/${business.slug}`} className={className} {...extraProps}>
        {children}
      </Link>
    );

  return (
    <article className="discover-card flex flex-col overflow-hidden rounded-card border border-ink-200 bg-white shadow-card transition duration-200 hover:shadow-card-hover">
      <div className="flex gap-4 p-4 sm:p-5">
        {/* The image goes where the title goes. It repeats the title's link, so
            it stays out of the tab order and the accessibility tree — a screen
            reader would otherwise meet the same destination twice, the first
            time with no name. */}
        {renderBusinessLink(
          image ? (
            <img
              src={image}
              alt=""
              loading="lazy"
              className="size-full object-cover transition duration-200 hover:scale-105"
            />
          ) : (
            <Monogram name={business.businessName} className="size-full" />
          ),
          "block size-20 shrink-0 overflow-hidden rounded-xl bg-ink-100 sm:size-24",
          { tabIndex: -1, "aria-hidden": true },
        )}

        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-ink-900">
            {renderBusinessLink(title, "block max-w-full truncate transition hover:text-brand-700")}
          </h3>

          {business.city ? (
            <p className="mt-1 flex items-center gap-1.5 text-fluid-sm text-ink-500">
              <i className="bx bx-map text-base" aria-hidden="true" />
              <span className="truncate">
                {business.county && business.county !== business.city
                  ? `${business.city}, ${business.county}`
                  : business.city}
              </span>
            </p>
          ) : null}

          {business.categories?.length ? (
            <ul className="mt-2.5 flex flex-wrap gap-1.5">
              {business.categories.slice(0, 3).map((category) => (
                <li
                  key={category}
                  className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700"
                >
                  {category}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {business.services?.length ? (
        <p className="truncate px-4 text-fluid-sm text-ink-500 sm:px-5">
          {business.services.map((service) => service.name).join(" · ")}
          {business.serviceCount > business.services.length
            ? ` +${business.serviceCount - business.services.length}`
            : ""}
        </p>
      ) : null}

      {availability ? (
        <div className="mt-4 border-t border-ink-100 bg-ink-50/60 p-4 sm:p-5">
          <p className="mb-2.5 flex flex-wrap items-baseline gap-x-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
            {isSoonest ? t("Discover.soonest") : t("Discover.freeSlots")}
            {isSoonest ? (
              <span className="text-sm font-semibold normal-case text-brand-700">
                {dayLabel()}
              </span>
            ) : null}
          </p>
          <ul className="flex flex-wrap gap-2">
            {slots.map((slot) => (
              <li key={slot.value}>{renderSlot(slot)}</li>
            ))}
            {remaining > 0 ? (
              <li className="self-center">
                {renderBusinessLink(
                  t("Discover.moreSlots", { count: remaining }),
                  "px-1 text-sm font-medium text-ink-500 transition hover:text-brand-700",
                )}
              </li>
            ) : null}
          </ul>
        </div>
      ) : (
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 p-4 pt-3 sm:p-5 sm:pt-3">
          {/* Saying the week is full is information; saying nothing looks like
              the card failed to load. */}
          {!date ? (
            <span className="text-fluid-sm text-ink-500">
              {t("Discover.noSlotsThisWeek")}
            </span>
          ) : null}
          {renderBusinessLink(
            <>
              {t("Discover.viewBusiness")}
              <i className="bi bi-arrow-right text-xs" aria-hidden="true" />
            </>,
            "inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 transition hover:text-brand-800",
          )}
        </div>
      )}
    </article>
  );
};

BusinessCard.propTypes = {
  business: PropTypes.shape({
    id: PropTypes.number,
    slug: PropTypes.string,
    businessName: PropTypes.string,
    bookingTitle: PropTypes.string,
    city: PropTypes.string,
    county: PropTypes.string,
    logoUrl: PropTypes.string,
    headerImageUrl: PropTypes.string,
    categories: PropTypes.arrayOf(PropTypes.string),
    serviceCount: PropTypes.number,
    services: PropTypes.array,
    availability: PropTypes.shape({
      date: PropTypes.string,
      slotCount: PropTypes.number,
      slots: PropTypes.array,
    }),
  }).isRequired,
  /** The searched day, or empty when the card is showing the soonest one. */
  date: PropTypes.string,
  /**
   * `(business, slot | null) => void`. Given, the card books in place: slots
   * become buttons and `slot` carries the resolved `date`. Omitted, the card
   * links to the business's public booking page instead.
   */
  onSelectSlot: PropTypes.func,
};

export default BusinessCard;
